import { storefront } from 'libs/storefront/client'
import { GET_PRODUCTS_BY_IDS, GET_PRODUCTS_SKU_BY_IDS } from '../../storefront/product.query'

/**
 * @typedef {import("@omneo/omneo-sdk").List[]} Lists
 * @typedef {import("../types/shopify").ShopifyStoreFrontProduct} ShopifyStoreFrontProduct
 * @typedef {import("../types/shopify").ShopifyStoreFrontProductResponse} ShopifyStoreFrontProductResponse
 */

export class OmneoWishlistParser {
  static PRODUCT_GUI_PREFIX = 'gid://shopify/Product/'

  #items
  /**
   * @param {Lists} items
   */
  constructor(items) {
    this.#items = items
  }

  /**
   * @param {Lists} lists
   * @return {Set<string>}
   */
  getUniqueProduct(lists) {
    return lists.reduce((acc, list) => {
      const { items } = list
      items.forEach((item) => acc.add(item.meta.productId))
      return acc
    }, new Set())
  }

  /**
   * @param {Set<string>} set
   * @returns {string[]}
   */
  parseSetToStringArray(set) {
    return Array.from(set.values())
  }

  generateMetafieldIdentifiers() {}

  async getShopifyProductSkus(ids) {
    const response = await storefront.query({
      query: GET_PRODUCTS_SKU_BY_IDS,
      variables: {
        ids
      }
    })
    return response
  }

  /**
   * @param {string[]} ids
   * @returns {Promise<ShopifyStoreFrontProductResponse>}
   */
  async getShopifyProductData(ids) {
    const response = await storefront.query({
      query: GET_PRODUCTS_BY_IDS,
      variables: {
        ids,
        metafieldIdentifiers: [
          {
            namespace: 'utopia',
            key: 'customer_facing_product_name'
          },
          {
            namespace: 'utopia',
            key: 'centimeter'
          },
          {
            namespace: 'utopia',
            key: 'kids_us'
          },
          {
            namespace: 'utopia',
            key: 'kids_uk'
          },
          {
            namespace: 'utopia',
            key: 'euro'
          },
          {
            namespace: 'utopia',
            key: 'age_group'
          },
          {
            namespace: 'omneo',
            key: 'brand'
          }
        ]
      }
    })
    return response
  }

  getIdFromGid(productGID) {
    return productGID.split('/').pop()
  }

  /**
   *
   * @param {{nodes: ShopifyStoreFrontProduct[]}} products
   */
  async parseShopifyProductResponse({ nodes }) {
    const object = nodes.reduce((acc, product) => {
      if (!product) return acc
      const id = this.getIdFromGid(product.id)
      const shopifyId = parseInt(id)
      const variants = product.variants.edges.map(({ node }) => node)
      acc[shopifyId] = {
        ...product,
        shopifyId,
        variants
      }
      return acc
    }, {})

    return object
  }

  async handleProductWishlistParse(ids, productMapData) {
    const shopifyGUI = this.parseIdToGID(ids)
    const shopifyApiResponse = await this.getShopifyProductSkus(shopifyGUI)
    if (!shopifyApiResponse?.data) return
    const parsed = await this.parseShopifyProductResponse(shopifyApiResponse?.data)

    const items = ids.reduce((acc, id) => {
      const product = parsed[id]
      const selectedVariants = productMapData[id]
      const variants = selectedVariants
        ?.map((variantId) => {
          const variant = product?.variants?.find((v) => this.getIdFromGid(v.id) === variantId)
          if (!variant && !variant?.availableForSale) {
            return { productId: '-1' }
          }

          return {
            productId: id,
            variantId: this.getIdFromGid(variant.id),
            img: product.featuredImage?.url,
            sku: variant.sku
          }
        })
        .filter((variant) => variant.productId !== '-1')

      return acc.concat(variants)
    }, [])
    return items
  }

  /**
   * @param {Lists} lists
   */
  async mapOmneoListData() {
    try {
      const getUniqueProductIds = this.getUniqueProduct(this.#items)
      const productIds = this.parseSetToStringArray(getUniqueProductIds)
      const shopifyGUI = this.parseIdToGID(productIds)
      const response = await this.getShopifyProductData(shopifyGUI)
      return this.parseShopifyProductResponse(response.data)
    } catch (err) {
      console.log(err)
    }
  }

  /**
   *
   * @param {string[]} items String[]
   */
  parseIdToGID(items) {
    return items.map((item) => OmneoWishlistParser.PRODUCT_GUI_PREFIX.concat(item))
  }
}
